/**
 * UploadProgress — progress bar + status message during upload and processing.
 */

import React from "react";
import { useTranslation } from "react-i18next";
import type { UploadState } from "../../store/conversionStore";

interface UploadProgressProps {
  state: UploadState;
  progress: number;  // 0–100
}

export const UploadProgress: React.FC<UploadProgressProps> = ({
  state,
  progress,
}) => {
  const { t } = useTranslation();

  if (state === "idle" || state === "done" || state === "error") {
    return null;
  }

  const label = t(`upload.${state}`);
  const displayProgress = state === "processing" ? null : progress;

  return (
    <div className="upload-progress" role="status" aria-live="polite">
      <p className="upload-progress__label">{label}</p>

      <div
        className="upload-progress__bar-track"
        role="progressbar"
        aria-valuenow={displayProgress ?? undefined}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className={[
            "upload-progress__bar-fill",
            displayProgress === null ? "upload-progress__bar-fill--indeterminate" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          style={
            displayProgress !== null
              ? { width: `${displayProgress}%` }
              : undefined
          }
        />
      </div>

      {displayProgress !== null && (
        <p className="upload-progress__percent">{displayProgress}%</p>
      )}
    </div>
  );
};
